import { CheckIcon } from "@phosphor-icons/react/Check";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ConfirmedField } from "@freightsimple/ui-core/Display/ConfirmedField";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";

import { LineItemsNmfcFreightClassQuestionBubble } from "../Bubbles/LineItemsNmfcFreightClassQuestionBubble";
import { LineItemsNmfcQuestionBubble } from "../Bubbles/LineItemsNmfcQuestionBubble";
import { LineItemsSelectNmfcSubItemDialog } from "../Dialogs/LineItemsSelectNmfcSubItemDialog";
import type { NmfcSubItemChoice } from "@freightsimple/generated-dashboard-openapi-client/models/NmfcSubItemChoice";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

// Errors surface after the field is blurred, or the parent forces validation,
// or the user pauses this long mid-typing (matches the old Cargo Input's
// `validateOnTimeoutMs`).
const VALIDATE_ON_TIMEOUT_MS = 10000;

function warningForMistakenlyEnteredFreightClass(
  nmfcCode: string,
): ReactNode | undefined {
  const freightClassOptions = [
    "50",
    "55",
    "60",
    "65",
    "70",
    "77.5",
    "85",
    "92.5",
    "100",
    "110",
    "125",
    "150",
    "175",
    "200",
    "250",
    "300",
    "400",
    "500",
  ];

  if (!freightClassOptions.includes(nmfcCode)) {
    return undefined;
  }

  return (
    <T size="xs" color="muted">
      If you have a 5-6 digit NMFC code, enter it, else leave blank
      <LineItemsNmfcFreightClassQuestionBubble />
    </T>
  );
}

function nmfcLabel(freightClass: string | undefined): ReactNode {
  const text =
    freightClass === undefined
      ? "NMFC Code"
      : `NMFC Code (Freight Class ${freightClass})`;
  // The bubble rides in the label rather than in FormItem's `tooltip` prop:
  // that prop renders a non-interactive tooltip, so the pointer can't reach
  // the Learn more link or select the text. Same as the sibling bubbles.
  return (
    <>
      {text}
      <LineItemsNmfcQuestionBubble />
    </>
  );
}

interface ConfirmedProps {
  value: string;
  onClear: () => void;
  description: string | undefined;
  freightClass: string | undefined;
}

function Confirmed_(props: ConfirmedProps) {
  return (
    <FormItem label={nmfcLabel(props.freightClass)}>
      <Stack gap={2}>
        <ConfirmedField
          value={props.value}
          onClear={props.onClear}
          clearTooltip="Clear NMFC code"
          clearTestId="clear-nmfc-code"
        />
        {props.description !== undefined && (
          // NMFC descriptions run to a paragraph — clipped to one line so the
          // confirmed code stays the focus of the row, with the full text on
          // hover.
          <Tooltip content={props.description} block>
            <Row gap={1} shrinkable>
              <Icon
                icon={CheckIcon}
                size="sm"
                weight="fill"
                color="text-dark-green"
              />
              <T size="sm" color="secondary" truncate>
                {props.description}
              </T>
            </Row>
          </Tooltip>
        )}
      </Stack>
    </FormItem>
  );
}

interface NmfcInputErrorProps {
  message: ReactNode;
  subItems: Array<NmfcSubItemChoice> | undefined;
  onSelectSubItem: (_: string) => void;
}

function NmfcInputError_(props: NmfcInputErrorProps) {
  const { subItems } = props;

  if (subItems) {
    return (
      <span>
        {props.message}.{" "}
        <Button
          size="link"
          inheritFont
          dialog={() => (
            <LineItemsSelectNmfcSubItemDialog
              subItems={subItems}
              onConfirm={props.onSelectSubItem}
            />
          )}
        >
          Select subitem
        </Button>
      </span>
    );
  }

  return <>{props.message}</>;
}

const PARTIAL_NMFC_REGEX = new RegExp(/^\d{0,6}(-\d?)?$/i);
const COMPLETE_NMFC_REGEX = new RegExp(/^\d{4,6}(-\d)?$/i);

interface NmfcInputProps {
  nmfcCode: string | undefined;
  onChange: (newValue: string | undefined) => void;
  loading: boolean;
  description: string | undefined;
  confirmed: boolean;
  errorMessage: ErrorMessageType;
  clearError: () => void;
  subItems: Array<NmfcSubItemChoice> | undefined;
  freightClass?: string | undefined;
}
function LineItemsNmfcInput(props: NmfcInputProps) {
  const [nmfcCode, setNmfcCode] = useState(props.nmfcCode || "");
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
  // Reveals the soft "did you mean a freight class?" warning — mirrors the old
  // Cargo Input, which validated on blur or after a typing pause.
  const [validate, setValidate] = useState(false);

  function enqueueChange(value: string) {
    const newId = setTimeout(function () {
      props.onChange(value);
    }, 2000);

    setTimeoutId(newId);
  }

  function clearQueuedChange() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(undefined);
    }
  }

  function triggerValidationImmediately(value: string) {
    clearQueuedChange();
    if (COMPLETE_NMFC_REGEX.test(value)) {
      props.onChange(value.replace(/^0+/, ""));
    }
  }

  useEffect(
    function () {
      setNmfcCode(props.nmfcCode || "");
    },
    [props.nmfcCode],
  );

  function onChange(value: string | undefined) {
    // Lock it while loading
    if (props.loading) {
      return;
    }

    if (value === undefined) {
      setNmfcCode("");
      props.onChange(undefined);
      return;
    }

    props.clearError();

    // Reveal the soft warning once the user stops typing for a while.
    setTimeout(function () {
      setValidate(true);
    }, VALIDATE_ON_TIMEOUT_MS);

    if (PARTIAL_NMFC_REGEX.test(value)) {
      setNmfcCode(value);
    }

    if (COMPLETE_NMFC_REGEX.test(value)) {
      clearQueuedChange();
      enqueueChange(value.replace(/^0+/, ""));
    } else {
      props.onChange(undefined);
      clearQueuedChange();
    }
  }

  if (props.confirmed) {
    return (
      <Confirmed_
        value={nmfcCode}
        onClear={function () {
          onChange(undefined);
        }}
        description={props.description}
        freightClass={props.freightClass}
      />
    );
  }

  const mistakenFreightClassWarning =
    warningForMistakenlyEnteredFreightClass(nmfcCode);
  const hasError = props.errorMessage !== undefined;

  // A real NMFC lookup error is a hard (red) error, unless the API offered
  // sub-items — then it's a soft (orange) "pick one" prompt, matching the old
  // Cargo `warn` flag. The mistaken-freight-class hint is always a soft warning.
  let error: ReactNode | undefined;
  let warning: ReactNode | undefined;
  if (hasError) {
    const content = (
      <NmfcInputError_
        message={props.errorMessage}
        subItems={props.subItems}
        onSelectSubItem={props.onChange}
      />
    );
    if (props.subItems !== undefined) {
      warning = content;
    } else {
      error = content;
    }
  } else if (validate && mistakenFreightClassWarning !== undefined) {
    warning = mistakenFreightClassWarning;
  }

  return (
    <ShowFormErrors show={hasError}>
      <FormItem
        label={nmfcLabel(props.freightClass)}
        error={error}
        warning={warning}
        fillWidth
      >
        <TextInput
          data-testid="nmfcInput"
          value={nmfcCode}
          placeholder="If known, enter the NMFC Code"
          loading={props.loading}
          onChange={function (e) {
            onChange(e.target.value);
          }}
          onBlur={function () {
            setValidate(true);
            if (nmfcCode && !props.loading) {
              triggerValidationImmediately(nmfcCode);
            }
          }}
        />
      </FormItem>
    </ShowFormErrors>
  );
}
export default LineItemsNmfcInput;
